import type { WithdrawalRequest } from '../types';

export type WithdrawalReviewKind = 'security' | 'amount' | 'general';

export interface WithdrawalReviewSummary {
  kind: WithdrawalReviewKind;
  title: string;
  shortReason: string;
  reasons: string[];
  flagCount: number;
}

const FRAUD_FLAG_TYPES = new Set([
  'abnormal_profit_ratio',
  'abnormal_absolute_profit',
  'new_account_high_profit',
  'repeated_high_profit',
  'rapid_deposit_withdrawal',
  'high_deposit_volume',
  'high_deposit_frequency',
]);

const FRIENDLY_FLAG_LABELS: Record<string, string> = {
  abnormal_profit_ratio: 'Withdrawals are much higher than deposits',
  abnormal_absolute_profit: 'Net winnings are unusually high',
  new_account_high_profit: 'New account with high winnings',
  repeated_high_profit: 'Player was flagged before for similar activity',
  rapid_deposit_withdrawal: 'Deposited and withdrew money very quickly',
  high_deposit_volume: 'Large total deposits in a short period',
  high_deposit_frequency: 'Many deposits in a short period',
  withdrawal_threshold: 'Withdrawal amount exceeds auto-approve limit',
};

function getFriendlyFlagLabel(flagType: string, description?: string): string {
  return FRIENDLY_FLAG_LABELS[flagType] || description || 'Needs manual review';
}

export function isWithdrawalUnderReview(withdrawal: WithdrawalRequest): boolean {
  if (withdrawal.status === 'awaiting_admin') return true;
  if (withdrawal.flaggedInfo?.isFlagged) return true;
  return false;
}

export function getWithdrawalReviewSummary(
  withdrawal: WithdrawalRequest,
): WithdrawalReviewSummary | null {
  if (!isWithdrawalUnderReview(withdrawal)) return null;

  const flagged = withdrawal.flaggedInfo;
  if (!flagged?.isFlagged) {
    return {
      kind: 'general',
      title: 'Needs review',
      shortReason: 'Waiting for admin approval',
      reasons: ['This withdrawal is paused until an admin reviews it.'],
      flagCount: 0,
    };
  }

  const flagTypes = flagged.flagTypes?.length
    ? flagged.flagTypes
    : (flagged.flags?.map((f) => f.flagType) ?? []);

  const reasonsFromApi =
    flagged.reasons?.map((r) => getFriendlyFlagLabel(r.flagType, r.description)) ?? [];

  const reasons = reasonsFromApi.length
    ? reasonsFromApi
    : flagTypes.map((ft) => getFriendlyFlagLabel(ft));

  const hasFraudFlags = flagTypes.some((t) => FRAUD_FLAG_TYPES.has(t));
  const amountOnly =
    !hasFraudFlags &&
    (Boolean(flagged.analysis?.withdrawalThresholdExceeded) ||
      flagTypes.includes('withdrawal_threshold'));

  const kind: WithdrawalReviewKind = hasFraudFlags
    ? 'security'
    : amountOnly || flagged.opsReviewOnly
      ? 'amount'
      : 'general';

  const title =
    kind === 'security'
      ? 'Security review'
      : kind === 'amount'
        ? 'Large amount review'
        : 'Needs review';

  const shortReason = reasons[0] || title;

  return {
    kind,
    title,
    shortReason,
    reasons: reasons.length ? reasons : [shortReason],
    flagCount: flagged.flags?.length ?? flagTypes.length,
  };
}

export function getWithdrawalReviewKindClassName(kind: WithdrawalReviewKind): string {
  switch (kind) {
    case 'security':
      return 'bg-orange-100 text-orange-900 border-orange-200';
    case 'amount':
      return 'bg-blue-100 text-blue-900 border-blue-200';
    default:
      return 'bg-amber-100 text-amber-900 border-amber-200';
  }
}
